import { memo, ReactElement, useCallback, useMemo, useRef } from 'react';
import { Button, Flex, Space, Tooltip } from 'antd';
import {
  CheckCircleOutlined,
  CloseCircleOutlined,
  DeleteOutlined,
  EditOutlined,
  EyeOutlined,
  FormOutlined,
  ShareAltOutlined,
  SyncOutlined,
  WarningOutlined,
} from '@ant-design/icons';
import { useTranslation } from 'react-i18next';

import { Table, TableComponent, TableProps, TableTagMappings, notification, CrudMode } from '@ohpen/enso-ui';
import { ActionEntityType, ActionStatus, ChangeRequestResponse, FieldType } from '@pmp/api';
import { useConfig } from '@ohpen/react-utils';
import { useAuth } from '@ohpen/react-auth';
import { getFilteredEntityTypeList, actionEntityTypeToPermissions } from '@pmp/utils';
import { Guard } from '@ohpen/react-authz';

import { columns } from '../tableConfig';
import { ChangeRequestsListProps, FlatChangeRequestData } from '../interfaces';

const canReviewStatus = new Set<ActionStatus>([ActionStatus.PENDING_REVIEW]);
const canEditStatus = new Set<ActionStatus>([ActionStatus.PENDING_REVIEW, ActionStatus.APPROVED, ActionStatus.PENDING_RESUBMISSION]);
const canDeleteStatus = new Set<ActionStatus>([ActionStatus.PENDING_REVIEW, ActionStatus.APPROVED, ActionStatus.PENDING_RESUBMISSION]);
const canApplyStatus = new Set<ActionStatus>([ActionStatus.APPROVED]);
const finalStatus = new Set<ActionStatus>([ActionStatus.APPLY_OK, ActionStatus.APPLY_OK_WITH_ERRORS, ActionStatus.APPLY_ERROR]);

const rowKey = 'actionId';
// List of entities with change requests. Should match with getColumnsByEntityType
const entityTypes = [
  ActionEntityType.PRODUCTS,
  ActionEntityType.ERC_PROFILES,
  ActionEntityType.FEES,
  ActionEntityType.FEE_PROFILES,
  ActionEntityType.EXCHANGE_RATES,
  ActionEntityType.BASE_RATES,
  ActionEntityType.INTEREST_RATES,
  ActionEntityType.EXCLUSIVITY_SETS,
  ActionEntityType.PROCURATION_FEE_RULES,
  ActionEntityType.DEFAULT_PROCURATION_FEE_ASSIGNMENTS,
  ActionEntityType.FIRM_PROCURATION_FEE_ASSIGNMENTS,
  ActionEntityType.LENDER_FIRMS,
  ActionEntityType.BROKERS,
];

export const ChangeRequestsList = memo(
  ({ state, valueLists, onView, onReview, onEdit, onDelete, onApply }: ChangeRequestsListProps): ReactElement => {
    const tableRef = useRef<TableComponent | null>(null);

    const { STAGE: stage } = useConfig();

    const { userName } = useAuth();

    const { t } = useTranslation();
    const isDev = stage === 'dev';

    const onRequestView = useCallback(
      (record: ChangeRequestResponse) => {
        if (!record.actionId || !record.actionEntityType.key || !record.status.key) return;
        const mode = finalStatus.has(record.status.key as ActionStatus) ? CrudMode.VIEW_RESULT : CrudMode.VIEW;
        onView(record, mode);
      },
      [onView],
    );

    const onRequestReview = useCallback(
      (record: ChangeRequestResponse) => {
        if (!record.actionId || !record.actionEntityType.key) return;
        onReview(record);
      },
      [onReview],
    );

    const onCopyLink = useCallback(
      (record: ChangeRequestResponse) => {
        if (!record.actionId || !record.actionEntityType.key) return;
        const url = `${globalThis.location.origin}/change-requests?search-change-requests=${encodeURIComponent(record.actionId)}`;
        navigator.clipboard.writeText(url).then(() => {
          notification().success({ message: t('global.notifications.linkCopied') });
        });
      },
      [t],
    );

    const onRequestEdit = useCallback(
      (record: ChangeRequestResponse) => {
        if (!record.actionId || !record.actionEntityType.key) return;
        onEdit(record);
      },
      [onEdit],
    );

    // const handleStatusFilterChange = useCallback(({ target: { value } }: RadioChangeEvent) => {
    //   setStatusFilter(value);
    // }, []);

    const getRowActions: (record: ChangeRequestResponse) => ReactElement[] = useCallback(
      record => {
        if (!record.status.key) return [];

        const isOwner = record.createdBy === userName;
        const canReview = canReviewStatus.has(record.status.key as ActionStatus) && (!isOwner || isDev);
        const canEdit = canEditStatus.has(record.status.key as ActionStatus) && isOwner && record.actionType.key !== 'DELETE';
        const canDelete = canDeleteStatus.has(record.status.key as ActionStatus) && isOwner;
        const canApply = canApplyStatus.has(record.status.key as ActionStatus) && isOwner;
        const isViewResult = finalStatus.has(record.status.key as ActionStatus);
        const entityType = actionEntityTypeToPermissions[record.actionEntityType.key as ActionEntityType];

        const rowActions = [
          <Tooltip key="view" title={t(isViewResult ? 'global.buttons.viewResult' : 'global.buttons.view')} placement="top">
            <Button
              aria-label="View change request"
              type="text"
              icon={<EyeOutlined />}
              onClick={() => onRequestView(record)}
              disabled={state.crudTable.isOpen || state.deleteModal.open}
            />
          </Tooltip>,
          <Tooltip key="share" title={t('global.buttons.copyLink')} placement="top">
            <Button
              aria-label="Copy link to change request"
              type="text"
              icon={<ShareAltOutlined />}
              onClick={() => onCopyLink(record)}
              disabled={state.crudTable.isOpen || state.deleteModal.open}
            />
          </Tooltip>,
          <Guard key="review" permissions={[`${entityType}:review`]}>
            <Tooltip title={t('global.buttons.review')} placement="top">
              <Button
                aria-label="Review change request"
                type="text"
                icon={<FormOutlined />}
                onClick={() => onRequestReview(record)}
                disabled={!canReview || state.crudTable.isOpen || state.deleteModal.open}
              />
            </Tooltip>
          </Guard>,
          <Guard key="edit" permissions={[`${entityType}:update`]}>
            <Tooltip title={t('global.buttons.edit')} placement="top">
              <Button
                aria-label="Edit change request"
                type="text"
                icon={<EditOutlined />}
                onClick={() => onRequestEdit(record)}
                disabled={!canEdit || state.crudTable.isOpen || state.deleteModal.open}
              />
            </Tooltip>
            <Tooltip key="apply" title={t('global.buttons.apply')} placement="top">
              <Button
                aria-label="Apply change request"
                type="text"
                icon={<CheckCircleOutlined />}
                onClick={() => onApply(record)}
                disabled={!canApply || state.crudTable.isOpen || state.deleteModal.open}
              />
            </Tooltip>
            <Tooltip key="delete" title={t('global.labels.delete')} placement="top">
              <Button
                type="text"
                aria-label="Delete change request"
                icon={<DeleteOutlined />}
                onClick={() => onDelete(record)}
                disabled={!canDelete || state.crudTable.isOpen || state.deleteModal.open}
              />
            </Tooltip>
          </Guard>,
        ];
        return rowActions;
      },
      [
        userName,
        isDev,
        t,
        state.crudTable.isOpen,
        state.deleteModal.open,
        onRequestView,
        onCopyLink,
        onRequestReview,
        onRequestEdit,
        onApply,
        onDelete,
      ],
    );

    // TODO refine this functionality
    // const filteredDataSource = useMemo(() => {
    //   if (statusFilter === 'all') return dataSource;
    //   return dataSource.filter(record => {
    //     const isOwner = record.createdBy === user?.username;
    //     const canReview = canReviewStatus.includes(record.status.key as ActionStatus); /* && !isOwner */
    //     const canEdit = canEditStatus.includes(record.status.key as ActionStatus); /* && isOwner */
    //     const endStatus: ActionStatus[] = [ActionStatus.Rejected, ActionStatus.ApplyOk, ActionStatus.ApplyOkWithErrors];

    //     return !endStatus.includes(record.status.key as ActionStatus) && ((canEdit && isOwner) || canReview);
    //   });
    // }, [dataSource, statusFilter, user?.username]);

    const tableProps: TableProps<ChangeRequestResponse> = useMemo(() => {
      const tagMappings: TableTagMappings = {
        status: {
          [ActionStatus.PENDING_REVIEW]: { color: 'orange', icon: <FormOutlined /> },
          [ActionStatus.PENDING_RESUBMISSION]: { color: 'purple', icon: <EditOutlined /> },
          [ActionStatus.APPROVED]: { color: 'success', icon: <CheckCircleOutlined /> },
          [ActionStatus.PENDING_PROCESSING]: { color: 'processing', icon: <SyncOutlined spin /> },
          [ActionStatus.PROCESSING]: { color: 'processing', icon: <SyncOutlined spin /> },
          [ActionStatus.APPLY_OK]: { color: '#00C08B', icon: <CheckCircleOutlined /> },
          [ActionStatus.APPLY_OK_WITH_ERRORS]: {
            color: '#FAAD14',
            icon: <WarningOutlined />,
            tooltip: { title: t('global.labels.changeRequestErrorTooltip') },
          },
          [ActionStatus.APPLY_ERROR]: {
            color: '#F5222D',
            icon: <CloseCircleOutlined />,
            tooltip: { title: t('global.labels.changeRequestErrorTooltip') },
          },
          [ActionStatus.DELETED]: { color: 'red', icon: <CloseCircleOutlined /> },
          [ActionStatus.REJECTED]: { color: 'red', icon: <CloseCircleOutlined /> },
        },
      };

      const flatDataSource: FlatChangeRequestData[] = state.dataSource.map(item => ({
        ...item,
        timeOfReview: item.reviewInfo?.timeOfReview || null,
        reviewedBy: item.reviewInfo?.reviewedBy || null,
      }));
      const filteredEntitiesValueLists = {
        ...valueLists,
        [FieldType.ACTION_ENTITY_TYPE]: getFilteredEntityTypeList(valueLists, entityTypes),
      };

      return {
        ref: tableRef,
        id: 'change-requests',
        itemsLabel: t('global.labels.changeRequests'),
        dataSource: flatDataSource,
        columns,
        rowKey,
        pagination: true,
        scroll: { x: 'max-content', y: 'table-height' },
        valueLists: filteredEntitiesValueLists,
        loading: state.isLoading,
        className: 'flat-table',
        tagMappings,
        getRowActions,
      };
    }, [getRowActions, state.dataSource, state.isLoading, t, valueLists]);

    // const statusFilterOptions = useMemo(
    //   () => [
    //     { label: t('global.labels.actionRequired'), value: 'actionRequired' },
    //     { label: t('global.labels.all'), value: 'all' },
    //   ],
    //   [t],
    // );

    return (
      <Table {...tableProps}>
        <Table.ActionsBar>
          <Flex justify="space-between">
            {/* <Radio.Group onChange={handleStatusFilterChange} options={statusFilterOptions} value={statusFilter} optionType="button" /> */}
            <Space size="middle">
              <Table.GlobalSearch />
            </Space>
            <Space size="middle">
              <Table.ResetFiltersButton />
              <Table.SelectColumnsButton />
            </Space>
          </Flex>
        </Table.ActionsBar>
      </Table>
    );
  },
);
